import { ChangeDetectionStrategy, Component, computed, input, model, output, signal } from '@angular/core';
import { SharedModule } from '@shared/shared.module';

/** Eén exportformaat in de dialoog, met de uitleg wat erin zit en wat niet. */
export interface EExportFormatOption {
  readonly id: string;
  /** Kale Font Awesome-naam, bv. `file-code`. */
  readonly icon: string;
  readonly title: string;
  /** Eén regel onder de titel (formaat, bestandsvorm). */
  readonly description: string;
  readonly includes: readonly string[];
  readonly excludes: readonly string[];
  /** Optionele slotregel onder de lijsten (bv. waarvoor het formaat bedoeld is). */
  readonly note?: string;
}

/** Vaste teksten; de host vertaalt ze in de taal van zijn eigen scherm. */
export interface EExportDialogLabels {
  readonly includes: string;
  readonly excludes: string;
  readonly download: string;
}

/**
 * Gedeelde exportdialoog (spec 2026-10-21-dagboek-momenten-export-design §4): kies een formaat
 * en lees vóór het downloaden wat de export bevat en wat niet. Met één formaat valt de keuze
 * weg en blijft enkel de uitleg. De host doet het eigenlijke exporteren op `exportRequested`
 * en sluit de dialoog zelf (of laat hem open bij een fout).
 */
@Component({
  selector: 'e-export-dialog',
  imports: [SharedModule],
  templateUrl: './e-export-dialog.component.html',
  styleUrl: './e-export-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EExportDialogComponent {
  readonly open = model(false);
  readonly title = input('');
  readonly intro = input('');
  readonly options = input.required<readonly EExportFormatOption[]>();
  readonly labels = input.required<EExportDialogLabels>();
  readonly busy = input(false);
  readonly exportRequested = output<string>();

  private readonly selectedId = signal<string | undefined>(undefined);

  protected readonly current = computed(() => {
    const options = this.options();
    return options.find(option => option.id === this.selectedId()) ?? options[0];
  });

  protected select(id: string): void {
    this.selectedId.set(id);
  }

  protected onOpenChange(open: boolean): void {
    this.open.set(open);
  }

  protected submit(): void {
    const option = this.current();
    if (!option || this.busy()) return;
    this.exportRequested.emit(option.id);
  }
}
